import { useMemo, useState } from 'react';
import { bumpPatch, type RuleSet } from '@kkp-fraud/shared';
import { ApiError, COOKIE_MODE, api, type ApiSettings } from '../api';
import { computeDiff, summariseDiff, type RuleDiff } from '../diff';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { t } from '../i18n';
import { fill } from './Interpolated';
import type { RuleSetDraft } from '../hooks/useRuleSetDraft';
import { findRule, reapplyOtherEdits } from '../scopedPublish';
import { useRuleCheck } from '../hooks/useRuleCheck';
import { fromValidation, problemText, type CheckResult } from '../ruleCheck';
import { CheckButton, CheckStatusLine, ProblemList } from './RuleCheckView';
import { ChangeRows } from './ProposalDiffView';
import { Tip } from './PageHead';
import { ConfirmDialog, useDialogBusy } from './Dialog';
import { When } from './When';

/**
 * The release dialog. Read the field-level diff and the engine's check, save — and the
 * document is live. Save / Publish is enabled only while the engine's latest answer is about
 * THIS document and says `ok` (Wave 2-0: the engine is the only rule check).
 *
 * Both auth modes render this screen:
 *   password mode — "Save & release", with a typed `actor` (the decision API's own route).
 *   cookie mode   — "Publish" through the BFF's `PUT /api/rules`. No actor field: the BFF
 *                   stamps it from the session. The maker-checker step is dropped for now
 *                   (product-owner decision), so Publish makes the draft live at once, then
 *                   the live document is re-read so every screen names the new version.
 */

function RuleDiffRow({ d }: { d: RuleDiff }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--line-2)' }} data-testid={`diff-${d.id}`}>
      <div className="row-wrap">
        <span className={`chip ${d.status === 'added' ? 'approve' : d.status === 'removed' ? 'decline' : 'notify'}`}>{t(`diff.status.${d.status}`)}</span>
        <span className="mono">{d.id}</span>
        <span>{d.name}</span>
        <span className="small muted">{d.kind === 'ruleBased' ? t('diff.ruleBased.label') : t('diff.patternBased.label')}</span>
      </div>
      {d.changes.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <ChangeRows changes={d.changes} />
        </div>
      )}
    </div>
  );
}

/** "RB-01: <rule name>" — the comment a rule row's Publish starts from. */
function scopedComment(candidate: RuleSet | undefined, id: string | undefined): string {
  if (!candidate || !id) return '';
  const rule = findRule(candidate, 'ruleBased', id) ?? findRule(candidate, 'patternBased', id);
  return rule?.name ? `${id}: ${rule.name}` : id;
}

export function ReviewSaveTab({
  state,
  settings,
  onToast,
  candidate,
  scopeLabel,
}: {
  state: RuleSetDraft;
  settings: ApiSettings;
  onToast: (message: string, kind?: 'ok' | 'error') => void;
  /** A rule row's Publish: release this document (the active one with one rule changed) instead of the draft. */
  candidate?: RuleSet;
  /** The id of the rule `candidate` releases (e.g. "RB-01"). */
  scopeLabel?: string;
}) {
  const [actor, setActor] = useLocalStorage<string>('kkp.rule-admin.actor', '');
  // Cookie mode names nobody by hand: the BFF takes the actor from the session.
  const needsActor = !COOKIE_MODE;
  const missingActor = needsActor && actor.trim().length === 0;
  const [comment, setComment] = useState(() => scopedComment(candidate, scopeLabel));
  /** The engine's refusal of a save (a 400 with `validation`) — shown until the next check answers. */
  const [rejected, setRejected] = useState<CheckResult | null>(null);
  const [busy, setBusy] = useState<'save' | null>(null);
  // Opened in the release dialog: Escape and the scrim wait for the publish to answer.
  useDialogBusy(busy !== null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** "Discard all changes" asks first (QA-R-03): it drops every unreleased edit of the set. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Scoped to one rule, everything below judges the document that will be saved.
  const draft = candidate ?? state.draft;
  const diff = useMemo(() => (candidate ? computeDiff(state.loaded, candidate) : state.diff), [candidate, state.loaded, state.diff]);
  const dirty = diff.dirty;
  // Scoped to one rule, the candidate gets its own check; otherwise the draft's (shared, so it
  // has usually answered before this dialog opens).
  const ownCheck = useRuleCheck(settings, candidate ?? null, { enabled: Boolean(candidate) });
  const check = candidate ? ownCheck : state.check;
  // A clean draft has nothing to check (the check is off then), so an earlier answer — about a
  // draft that was just discarded or published — is not shown under "no changes" (QA-R-04).
  const shown = !dirty ? null : rejected && rejected.hash === check.result?.hash ? rejected : check.result;
  const blocked = !check.canSave;
  // The version this release will carry. Named up front, because a release is the whole
  // document, not the diff on screen.
  const nextVersion = state.loaded ? bumpPatch(state.loaded.version) : '—';

  const save = async () => {
    if (!draft) return;
    setBusy('save');
    setSaveError(null);
    setRejected(null);
    try {
      // The version the draft was loaded from — the API answers 409 rather than letting us
      // silently overwrite somebody who released while this draft was open.
      const oldLoaded = state.loaded;
      const oldDraft = state.draft;
      const res = await api.putRules(settings, draft, actor.trim(), comment.trim() || undefined, state.loaded?.version);
      if (COOKIE_MODE) {
        // The engine has moved: re-read the live document so the header, every screen's copy
        // of it and the version history all name the version that is actually running.
        // `reload()` also resets the draft to it — clean — so any re-apply comes after.
        const live = await state.reload();
        // A rule row's publish carried one rule; the draft's other edits are still unpublished.
        if (candidate && scopeLabel && live && oldLoaded && oldDraft) state.setDraft(reapplyOtherEdits(live, oldLoaded, oldDraft, scopeLabel));
        onToast(
          typeof res.activeWithinSeconds === 'number'
            ? t('release.published-within.toast', { version: res.version, seconds: res.activeWithinSeconds })
            : t('release.published.toast', { version: res.version }),
          'ok',
        );
      } else {
        const saved = { ...draft, version: res.version, updatedUtc: new Date().toISOString() };
        state.commit(saved);
        // A rule row's release carried one rule; the draft's other edits are still unreleased.
        if (candidate && scopeLabel && oldLoaded && oldDraft) state.setDraft(reapplyOtherEdits(saved, oldLoaded, oldDraft, scopeLabel));
        onToast(t('release.saved.toast', { version: res.version }), 'ok');
      }
      setComment('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const released = e.activeVersion ?? t('release.newer-version.fallback');
        setSaveError(t('release.conflict.message', { version: released, reload: t('btn.reload.label') }));
        onToast(t('release.conflict.toast', { version: released }), 'error');
      } else if (e instanceof ApiError && e.validation) {
        if (check.result) setRejected(fromValidation(check.result.hash, e.validation));
        setSaveError(t('release.rejected.message', { n: e.validation.errors.length }));
        onToast(t('release.failed.toast'), 'error');
      } else {
        setSaveError((e as Error).message);
        onToast(t('release.failed.toast'), 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  if (!draft) return <div className="empty">{t('release.no-document.empty')}</div>;

  return (
    <div className="grid g2 aside">
      <div>
        <section className="card">
          <header className="card-head">
            <h2>{t('release.changes.title')}</h2>
            <span className={`chip ${dirty ? 'notify' : ''}`} data-testid="diff-summary">
              {summariseDiff(diff)}
            </span>
            <span className="spacer" />
            {!candidate && state.dirty && (
              <button type="button" className="btn" onClick={() => setConfirmDiscard(true)}>
                {t('btn.discard.label')}
              </button>
            )}
          </header>
          <div className="card-body">
            {!dirty && <div className="empty">{t('release.clean.empty')}</div>}

            {diff.meta.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div className="note" style={{ margin: '0 0 4px' }}>
                  {t('diff.meta.title')}
                </div>
                <ChangeRows changes={diff.meta} />
              </div>
            )}

            {diff.rules.map((d) => (
              <RuleDiffRow key={`${d.kind}-${d.id}`} d={d} />
            ))}
          </div>
        </section>

        {shown && (
          <section className="card" data-testid="check-result">
            <header className="card-head">
              <h2>{t('release.validation.title')}</h2>
              <span className={`chip ${shown.ok ? 'approve' : 'decline'}`}>
                {shown.ok
                  ? t('validation.passes.state')
                  : t(shown.problems.length === 1 ? 'validation.errors.state.one' : 'validation.errors.state', { n: shown.problems.length })}
              </span>
            </header>
            <div className="card-body stack">
              {shown.problems.length > 0 && (
                <div className="banner error" role="alert" data-testid="check-problems">
                  <strong>{t('validation.errors.title')}</strong>
                  <ProblemList problems={shown.problems} />
                </div>
              )}
              {shown.warnings.length > 0 && (
                <div className="banner warn">
                  <strong>{t('validation.warnings.release')}</strong>
                  <ul>
                    {shown.warnings.map((w, i) => (
                      <li key={i} className="small">
                        {problemText(w)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {shown.ok && shown.warnings.length === 0 && <div className="banner ok">{t('validation.clean.note')}</div>}
            </div>
          </section>
        )}
      </div>

      <div>
        <section className="card">
          <header className="card-head">
            <h2>{t('release.panel.title')}</h2>
          </header>
          <div className="card-body stack">
            {needsActor && (
              <label className="field">
                {t('release.actor.label')}
                <input
                  type="text"
                  aria-label={t('form.actor.a11y')}
                  placeholder={t('release.actor.hint')}
                  value={actor}
                  onChange={(e) => setActor(e.target.value)}
                />
              </label>
            )}
            <label className="field">
              {t('release.comment.label')}
              <textarea
                aria-label={t('form.comment.a11y')}
                placeholder={t('release.comment.hint')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </label>

            <CheckStatusLine check={check} />

            {saveError && (
              <div className="banner error" role="alert">
                {saveError}
              </div>
            )}

            <div className="row">
              <CheckButton check={check} />
              <button
                type="button"
                className="btn primary"
                data-testid="save-button"
                onClick={save}
                disabled={busy !== null || blocked || !dirty || missingActor}
                title={
                  blocked
                    ? check.status === 'failed'
                      ? t('problem.check.failed')
                      : check.status === 'done'
                        ? t('release.blocked.tip')
                        : t('problem.check.running')
                    : !dirty
                      ? t('btn.nothing-changed.tip')
                      : missingActor
                        ? t('release.no-actor.tip')
                        : t('release.save.tip')
                }
              >
                {COOKIE_MODE
                  ? busy === 'save'
                    ? t('btn.publishing.label')
                    : t('btn.publish.label')
                  : busy === 'save'
                    ? t('btn.saving.label')
                    : t('btn.save-release.label')}
              </button>
            </div>

            {!candidate && (
              <div className="small muted" data-testid="whole-document-note">
                {fill(t('release.next.line'), { version: <span className="mono">{nextVersion}</span> })}{' '}
                <Tip>{t('release.whole-doc.tip', { version: state.loaded?.version ?? '—' })}</Tip>
              </div>
            )}
          </div>
        </section>

        <section className="card">
          <header className="card-head">
            <h2>{t('release.live.title')}</h2>
          </header>
          <div className="card-body">
            <div className="kv-row">
              <span className="k">{t('col.version.label')}</span>
              <span className="v">{state.loaded?.version ?? '—'}</span>
            </div>
            <div className="kv-row">
              <span className="k">{t('release.live.source')}</span>
              <span className="v">{state.source ?? '—'}</span>
            </div>
            <div className="kv-row">
              <span className="k">{t('release.live.loadedAt')}</span>
              <span className="v">
                <When iso={state.loadedAt} />
              </span>
            </div>
            <div className="kv-row">
              <span className="k">{t('release.live.updatedUtc')}</span>
              <span className="v">
                <When iso={state.loaded?.updatedUtc} />
              </span>
            </div>
          </div>
        </section>
      </div>

      {confirmDiscard && (
        <ConfirmDialog
          testid="discard-all"
          title={t('release.discard.title')}
          confirmLabel={t('btn.discard.label')}
          cancelLabel={t('release.discard.keep')}
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={() => {
            setConfirmDiscard(false);
            setRejected(null);
            setSaveError(null);
            state.revert();
          }}
        >
          <p className="small">{t('release.discard.body')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
